/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import type {
  DOMAttributes,
  LabelHTMLAttributes,
  ReactNode,
  RefObject,
} from "react";

import { cssFns } from "@superweb/css";

import { useUiColors } from "../theme";
import {
  ClearButton,
  FieldDescription,
  FieldErrorMessage,
  FieldLabel,
} from "../fields";

export const FieldLayout = ({
  fieldProps,
  fieldRef,

  shrunk,
  focused,
  disabled = false,

  icon,
  input,
  toolbar,

  label,
  labelProps,
  description,
  descriptionProps,
  errorMessage,
  errorMessageProps,
  clearButtonVisible,
  onClearButtonPress,
}: {
  fieldProps?: DOMAttributes<Element>;
  fieldRef?: RefObject<HTMLDivElement>;

  shrunk: boolean;
  focused: boolean;
  disabled?: boolean;

  icon?: ReactNode;
  input: ReactNode;
  toolbar: ReactNode;

  label: string;
  labelProps: LabelHTMLAttributes<HTMLLabelElement>;

  description?: string;
  descriptionProps?: DOMAttributes<Element>;

  errorMessage?: string;
  errorMessageProps?: DOMAttributes<Element>;

  clearButtonVisible: boolean;
  onClearButtonPress: () => void;
}) => {
  const uiColors = useUiColors();

  return (
    <div
      css={{
        display: "inline-grid",
      }}
    >
      <div
        {...fieldProps}
        ref={fieldRef}
        css={{
          height: "56px",
          boxSizing: "border-box",

          display: "grid",
          gridTemplateColumns: icon
            ? "[icon] min-content [value] 1fr [clear] min-content [toolbar] min-content [end]"
            : "[value] 1fr [clear] min-content [toolbar] min-content [end]",

          ...cssFns.border({
            width: "2px",
            radius: "16px",
            style: "solid",
            color: focused ? uiColors.text : "transparent",
          }),

          backgroundColor: focused
            ? uiColors.backgroundFloating
            : uiColors.controlMinor,
          opacity: disabled ? "0.5" : "1",
        }}
      >
        <div
          css={{
            gridColumnStart: "value",
            gridColumnEnd: "toolbar",
            gridRowStart: "1",
            gridRowEnd: "2",
            alignSelf: "start",
            marginBlockStart: "18px",
            marginInlineStart: icon ? "8px" : "14px",
            pointerEvents: "none",
          }}
        >
          <FieldLabel shrunk={shrunk} labelProps={labelProps}>
            {label}
          </FieldLabel>
        </div>

        {icon ? (
          <div
            css={{
              gridColumnStart: "1",
              gridColumnEnd: "value",
              gridRowStart: "1",
              gridRowEnd: "2",
              alignSelf: "start",
              marginBlockStart: "6px",
              marginInlineStart: "6px",
              color: uiColors.text,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
            }}
          >
            {icon}
          </div>
        ) : undefined}

        <div
          css={{
            height: "100%",
            boxSizing: "border-box",
            gridColumnStart: "1",
            gridColumnEnd: "toolbar",
            gridRowStart: "1",
            gridRowEnd: "2",
            alignSelf: "start",
            color: uiColors.text,
            opacity: shrunk ? "1" : "0",
            whiteSpace: "nowrap",
          }}
        >
          {input}
        </div>

        <div
          css={{
            gridColumnStart: "clear",
            gridColumnEnd: "toolbar",
            gridRowStart: "1",
            gridRowEnd: "2",
            alignSelf: "center",
            marginInlineStart: "8px",
          }}
        >
          <ClearButton
            visible={clearButtonVisible}
            onPress={onClearButtonPress}
          />
        </div>

        <div
          css={{
            display: "grid",
            marginInlineEnd: "8px",
            gridColumnStart: "toolbar",
            gridColumnEnd: "end",
            gridRowStart: "1",
            gridRowEnd: "2",
            alignSelf: "center",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {toolbar}
        </div>
      </div>

      {errorMessage || description ? (
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {errorMessage ? (
            <FieldErrorMessage errorMessageProps={errorMessageProps}>
              {errorMessage}
            </FieldErrorMessage>
          ) : (
            description && (
              <FieldDescription descriptionProps={descriptionProps}>
                {description}
              </FieldDescription>
            )
          )}
        </div>
      ) : null}
    </div>
  );
};
